import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Measurement } from '@shared/types.ts';
import type { MeasurementKind } from '@shared/constants.ts';
import { api } from './client.ts';

export function useMeasurements(babyId: string | null | undefined, kind?: MeasurementKind) {
  return useQuery({
    queryKey: ['measurements', { babyId, kind: kind ?? null }],
    enabled: !!babyId,
    queryFn: () => {
      const p = new URLSearchParams({ baby: babyId! });
      if (kind) p.set('kind', kind);
      return api<{ measurements: Measurement[] }>(`/api/measurements?${p}`);
    },
    select: (d) => d.measurements,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['measurements'] });
}

export function useCreateMeasurement() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (vars: {
      kind: MeasurementKind;
      value: number;
      occurred_at?: number;
      notes?: string | null;
    }) =>
      api<{ measurement: Measurement }>('/api/measurements', {
        method: 'POST',
        json: vars,
      }),
    onSuccess: () => inv(),
  });
}

export function useUpdateMeasurement() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api<{ measurement: Measurement }>(`/api/measurements/${id}`, {
        method: 'PATCH',
        json: patch,
      }),
    onSuccess: () => inv(),
  });
}

export function useDeleteMeasurement() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api(`/api/measurements/${id}`, { method: 'DELETE' }),
    onSuccess: () => inv(),
  });
}
